'use client';

import * as React from 'react';
import { useSyncExternalStore } from 'react';

import {
  isConnectionUnstable,
  resetNetworkState,
  subscribeToNetworkState,
} from '../../infrastructure/api/network/networkState';
import {
  isWaitingForNetwork,
  subscribeToWaitingForNetwork,
} from '../../infrastructure/offline';
type NetworkConnectionInfo = EventTarget & {
  readonly effectiveType?: string;
  readonly downlink?: number;
  readonly saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  readonly connection?: NetworkConnectionInfo;
  readonly mozConnection?: NetworkConnectionInfo;
  readonly webkitConnection?: NetworkConnectionInfo;
};

function readNavigatorConnection(): NetworkConnectionInfo | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const nav = navigator as NavigatorWithConnection;
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
}

function readIsSlowConnection(): boolean {
  const connection = readNavigatorConnection();
  if (!connection) {
    return false;
  }

  if (connection.saveData === true) {
    return true;
  }

  const effectiveType = connection.effectiveType?.toLowerCase();
  if (effectiveType === 'slow-2g' || effectiveType === '2g') {
    return true;
  }

  const downlink = connection.downlink;
  return typeof downlink === 'number' && downlink > 0 && downlink < 1;
}

function subscribeToOnlineStatus(onStoreChange: () => void): () => void {
  globalThis.addEventListener('online', onStoreChange);
  globalThis.addEventListener('offline', onStoreChange);
  return () => {
    globalThis.removeEventListener('online', onStoreChange);
    globalThis.removeEventListener('offline', onStoreChange);
  };
}

function getOnlineSnapshot(): boolean {
  return navigator.onLine;
}

function getOnlineServerSnapshot(): boolean {
  // Assume online during SSR — avoids false offline banners before hydration.
  return true;
}

export type NetworkStatus = {
  isOnline: boolean;
  isSlowConnection: boolean;
  isConnectionUnstable: boolean;
};

/**
 * Tracks browser connectivity and recent transport failures for global UX.
 */
export function useNetworkStatus(): NetworkStatus {
  const isBrowserOnline = useSyncExternalStore(
    subscribeToOnlineStatus,
    getOnlineSnapshot,
    getOnlineServerSnapshot,
  );
  // A saved copy the service worker served because the network failed is
  // offline for certain, whatever navigator.onLine says, until the network
  // answers again.
  const isShowingOfflineCopy = useSyncExternalStore(
    subscribeToWaitingForNetwork,
    isWaitingForNetwork,
    () => false,
  );
  const isOnline = isBrowserOnline && !isShowingOfflineCopy;

  const [isSlowConnection, setIsSlowConnection] = React.useState(false);
  const [isUnstable, setIsUnstable] = React.useState(false);
  const [hasEvaluatedConnection, setHasEvaluatedConnection] =
    React.useState(false);

  React.useEffect(() => {
    setHasEvaluatedConnection(true);
    setIsSlowConnection(readIsSlowConnection());
    setIsUnstable(isConnectionUnstable());

    const handleOnline = (): void => {
      resetNetworkState();
      setIsUnstable(false);
    };

    const handleConnectionChange = (): void => {
      setIsSlowConnection(readIsSlowConnection());
    };

    const unsubscribeNetworkState = subscribeToNetworkState(() => {
      setIsUnstable(isConnectionUnstable());
    });

    const connection = readNavigatorConnection();
    connection?.addEventListener?.('change', handleConnectionChange);

    globalThis.addEventListener('online', handleOnline);

    return () => {
      globalThis.removeEventListener('online', handleOnline);
      connection?.removeEventListener?.('change', handleConnectionChange);
      unsubscribeNetworkState();
    };
  }, []);

  return {
    isOnline,
    isSlowConnection: hasEvaluatedConnection && isSlowConnection,
    isConnectionUnstable: hasEvaluatedConnection && isOnline && isUnstable,
  };
}
